import { useEffect, useRef } from "react";

interface UseScrollLoadMoreOptions {
  containerRef: React.RefObject<HTMLElement>;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number;
}

export const useScrollLoadMore = ({
  containerRef,
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 200,
}: UseScrollLoadMoreOptions) => {
  const loadingRef = useRef(false);
  const hasInitialContentRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!hasMore || isLoading || loadingRef.current) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollHeight <= clientHeight && !hasInitialContentRef.current) {
        return;
      }
      if (scrollHeight > clientHeight) {
        hasInitialContentRef.current = true;
      }

      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      if (distanceFromBottom < threshold) {
        loadingRef.current = true;
        onLoadMore();
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    const rafId = requestAnimationFrame(() => {
      handleScroll();
    });

    return () => {
      container.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [containerRef, hasMore, isLoading, onLoadMore, threshold]);

  useEffect(() => {
    if (!isLoading) {
      loadingRef.current = false;
    }
  }, [isLoading]);
};
